import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays, Filter, RotateCcw, ExternalLink, AlertCircle,
  Cloud, CheckCircle2, XCircle, Clock, UserX,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const RANGE_OPTIONS = [
  { value: "upcoming", label: "Kommende Termine" },
  { value: "today", label: "Heute" },
  { value: "week", label: "Diese Woche" },
  { value: "past", label: "Vergangene Termine" },
  { value: "all", label: "Alle" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Alle Status" },
  { value: "proposed", label: "Vorgeschlagen" },
  { value: "booked", label: "Gebucht" },
  { value: "completed", label: "Durchgeführt" },
  { value: "cancelled", label: "Abgesagt" },
  { value: "no_show", label: "No-Show" },
];

const STATUS_TONE = {
  proposed: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  booked: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  completed: "border-zinc-500/40 bg-zinc-700/20 text-zinc-300",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-300",
  no_show: "border-amber-500/40 bg-amber-500/10 text-amber-300",
};

const STATUS_ICON = {
  proposed: Clock,
  booked: CalendarDays,
  completed: CheckCircle2,
  cancelled: XCircle,
  no_show: UserX,
};

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function computeRange(value) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (value === "today") {
    const end = new Date(startOfDay);
    end.setDate(end.getDate() + 1);
    return { from: startOfDay.toISOString(), to: end.toISOString() };
  }
  if (value === "week") {
    const end = new Date(startOfDay);
    end.setDate(end.getDate() + 7);
    return { from: startOfDay.toISOString(), to: end.toISOString() };
  }
  if (value === "upcoming") return { from: now.toISOString(), to: null };
  if (value === "past") return { from: null, to: now.toISOString() };
  return { from: null, to: null };
}

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("upcoming");
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [googleStatus, setGoogleStatus] = useState(null);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      const r = computeRange(range);
      if (r.from) params.set("from_date", r.from);
      if (r.to) params.set("to_date", r.to);
      if (status && status !== "all") params.set("status", status);
      params.set("page_size", "100");
      const res = await axios.get(`${API}/admin/crm/calendar?${params}`, {
        headers: authHeaders(), timeout: 20000,
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Fehler");
    } finally {
      setLoading(false);
    }
  }, [range, status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/admin/crm/calendar/google/status`, { headers: authHeaders(), timeout: 10000 })
      .then((r) => setGoogleStatus(r.data))
      .catch(() => setGoogleStatus(null));
  }, []);

  const filtered = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    if (!term) return items;
    return items.filter((it) => {
      const blob = [
        it.title, it.lead?.full_name, it.lead?.company,
        it.location, it.note,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(term);
    });
  }, [items, searchInput]);

  return (
    <div data-testid="journey-calendar-page" className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-zinc-100">Termine</h1>
        <p className="text-sm text-zinc-400">
          Alle geplanten Gespräche und CRM-Termine aus PINE Journey Control.
        </p>
      </header>

      <section className="space-y-3 rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <FilterSelect testId="journey-calendar-filter-range" value={range} onChange={setRange} options={RANGE_OPTIONS} />
          <FilterSelect testId="journey-calendar-filter-status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Suche nach Lead, Firma, Titel…"
            data-testid="journey-calendar-search"
            className="rounded border border-white/10 bg-[#0B0F10] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
          <button
            type="button"
            onClick={load}
            data-testid="journey-calendar-refresh"
            className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-white/20 hover:bg-white/5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Aktualisieren
          </button>
        </div>
      </section>

      {googleStatus ? (
        <section
          data-testid="journey-calendar-google-box"
          className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Cloud className="h-5 w-5 text-sky-400" />
              <div>
                <div className="text-sm text-zinc-200">Google Calendar Sync</div>
                <div className="text-[11px] text-zinc-500">
                  Status:{" "}
                  <span data-testid="journey-calendar-google-status" className="text-amber-300">
                    Noch nicht verbunden
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              disabled
              data-testid="journey-calendar-google-connect"
              className="cursor-not-allowed rounded border border-white/10 px-3 py-1.5 text-[11px] text-zinc-500"
              title="Wird in einer späteren Iteration aktiviert"
            >
              Google Sync kommt später
            </button>
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">{googleStatus.message}</p>
        </section>
      ) : null}

      {error ? (
        <div data-testid="journey-calendar-error" className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-4 text-sm text-red-200">
          <div className="mb-1 flex items-center gap-2 font-medium">
            <AlertCircle className="h-4 w-4" />
            Fehler beim Laden
          </div>
          <div className="text-xs">{String(error)}</div>
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded bg-white/[0.03]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/[0.06] p-8 text-center text-sm text-zinc-400">
          <CalendarDays className="mx-auto mb-2 h-6 w-6 text-zinc-600" />
          Keine Termine im gewählten Zeitraum.
        </div>
      ) : (
        <ul data-testid="journey-calendar-list" className="space-y-2">
          {filtered.map((ev) => {
            const Icon = STATUS_ICON[ev.meeting_status] || CalendarDays;
            return (
              <li
                key={ev.id}
                data-testid={`journey-calendar-item-${ev.id}`}
                className="rounded-md border border-white/[0.06] bg-white/[0.01] p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-zinc-100">{ev.title}</span>
                      <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] ${STATUS_TONE[ev.meeting_status] || ""}`}>
                        <Icon className="h-3 w-3" />
                        {STATUS_OPTIONS.find((o) => o.value === ev.meeting_status)?.label || ev.meeting_status}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400">
                      {fmt(ev.starts_at)}
                      {ev.location ? <> · {ev.location}</> : null}
                    </div>
                    {ev.lead?.lead_id ? (
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                        {ev.lead.full_name || "—"}
                        {ev.lead.company ? <span>· {ev.lead.company}</span> : null}
                        <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-zinc-300">
                          {ev.lead.kind_label}
                        </span>
                        <span className="rounded border border-white/5 px-1.5 py-0.5 text-[10px] text-zinc-500">
                          {ev.lead.pipeline_stage_label}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {ev.meeting_link ? (
                      <a
                        href={ev.meeting_link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`journey-calendar-open-${ev.id}`}
                        className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-500/20"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Meeting
                      </a>
                    ) : null}
                    {ev.lead?.lead_id ? (
                      <Link
                        to={`/admin/journey/leads/${ev.lead.lead_id}`}
                        data-testid={`journey-calendar-lead-${ev.id}`}
                        className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 text-[11px] text-zinc-300 hover:border-white/20 hover:bg-white/5"
                      >
                        Lead öffnen
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="text-right text-xs text-zinc-500">
        {filtered.length} angezeigt · {total} insgesamt
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Filter className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full rounded border border-white/10 bg-[#0B0F10] px-2 py-2 text-xs text-zinc-200 outline-none focus:border-emerald-500/40"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
